// -- Risk Score: per-user and org-wide -------------------------------------
// Five weighted behavioural signals over a rolling 6-month (180-day) window.
// Lower score = lower risk. Final per-user score clamped to 0-100.
//
// This file now computes over the LIVE rows hydrated into ./data (SEND_LOG,
// COURSE_SEND_LOG, USERS) rather than the prototype's mock dataset. The
// formulas, weights, bands and sensitivity table are unchanged - they remain
// the v1 contract. The shell calls recomputeRiskModel() after every
// hydration; all derived exports are live `let` bindings.
//
// Differences from the prototype now that data is real:
//   - previousBand (sensitivity multiplier input) is computed from a window
//     shifted 30 days back instead of being seeded from USERS.lv - i.e. a
//     genuine "previous cycle" readout.
//   - ORG_TREND and buildFactorTrend recompute the model against month-end
//     windows over the real events (the prototype synthesised a walk).
//   - BENCHMARK platform anchors come from the dashboard endpoint's
//     anonymous cross-tenancy 'benchmark' aggregation. There is NO industry
//     data source on the platform (no tenancy industry column exists), so
//     industry anchors are null and the industryBenchmarking feature
//     defaults off.
//
// Calculations summary (full per-signal formulae in SIGNALS array):
//   - fail-rate (35%): failed / sims in window. fail = status in
//     {clicked, clicked_2nd, attachment}.
//   - repeat (20%): share of tests inside a 2+ consecutive-click run.
//   - stage-depth (20%): max STAGE_DEPTH_MAP across fails
//     {clicked: 0.5, attachment: 0.5, clicked_2nd: 1.0}.
//   - training-debt (15%): overdue / total courses; overdue = uncompleted
//     and sent > COURSE_DUE_DAYS (14d) ago.
//   - course-lag (10%): avg days to completion / 30, capped at 1.
//   - baseScore = sum(weight * component). adjusted = base * previousBand
//     multiplier (Low 1.5, Medium 1.0, High 0.85, Critical 0.7). Final
//     clamped 0-100. Org score = plain mean of scorable users (the v2
//     volume floor was removed 2026-08).
//   - bands: Low 0-29, Medium 30-55, High 56-75, Critical 76-100.

import { USERS, SEND_LOG, COURSE_SEND_LOG } from './data';

// Anchored to the wall clock at recompute time (the prototype pinned this to
// its mock dataset's seed date).
let DATA_TODAY = new Date();

const WINDOW_DAYS = 180;
export const SCORE_WINDOW_DAYS = WINDOW_DAYS;
export const SCORE_WINDOW_LABEL = '6 months';
const COURSE_DUE_DAYS = 14;       // a course is "overdue" if sent >14d ago and uncompleted
// Spec calls for >=3 sims in window before scoring. Kept at 1 while live
// tenancies ramp up send volume - with sparse dev data a gate of 3 would mark
// nearly every user "Insufficient data". Restore to 3 once campaign cadence
// guarantees 3+ sims per user per window.
const MIN_SIMS_FOR_SCORE = 1;

// -- Bands -----------------------------------------------------------------
export const BANDS = [
    { name: 'Low',      min: 0,  max: 29,  color: '#66BB6A', textColor: '#388E3C', bg: '#E8F5E9', border: 'transparent', multiplier: 1.5  },
    { name: 'Medium',   min: 30, max: 55,  color: '#FFCA28', textColor: '#C68A00', bg: '#FFF8E1', border: 'transparent', multiplier: 1.0  },
    { name: 'High',     min: 56, max: 75,  color: '#FF7043', textColor: '#BF360C', bg: '#FFCCBC', border: 'transparent', multiplier: 0.85 },
    { name: 'Critical', min: 76, max: 100, color: '#FF5252', textColor: '#E53935', bg: '#FFEBEE', border: 'transparent', multiplier: 0.7  },
];

export function bandFor(score) {
    if (score == null) return null;
    return BANDS.find((b) => score >= b.min && score <= b.max) || BANDS[BANDS.length - 1];
}

// -- Signals ---------------------------------------------------------------
// Weights MUST sum to 100. anchor.platform is populated at recompute time
// from the cross-tenancy benchmark aggregation; anchor.industry stays null
// (no industry data source on the platform). anchor.target is the product
// target and stays a constant.
export const SIGNALS = [
    {
        id: 'fail-rate', label: 'Failure rate', weight: 35,
        meaning: 'of phishing tests are failed',
        description: 'Failed tests divided by total tests delivered in the rolling 6-month window. Sim-type agnostic.',
        soWhat: 'Assign a remedial sim plus the Phishing Basics course to users above the benchmark.',
        anchor: { industry: null, platform: null, target: 0.10, format: 'percent', goodBelow: true },
    },
    {
        id: 'repeat', label: 'Repeat offender rate', weight: 20,
        meaning: 'of tests landed in a back-to-back click streak',
        description: 'Share of the tests in the rolling 6-month window that fall inside a run of two or more consecutive clicks, ordered by send date. Isolated clicks are excluded; the value rises as click streaks lengthen and recur.',
        soWhat: 'The clicking is forming a pattern, training is not landing. Review their course history and escalate repeat streaks to their manager.',
        anchor: { industry: null, platform: null, target: 0.03, format: 'percent', goodBelow: true },
    },
    {
        id: 'stage-depth', label: 'Stage depth', weight: 20,
        meaning: 'severity on the worst failed test',
        description: 'How far the user got on their worst failed test in the rolling 6-month window, across two stages: clicked the link in the email (50%), continued through to the landing page (100%). Worst single test, not an average. We record the stage reached, not what was entered.',
        soWhat: 'Run a credential-harvest sim to test where the biggest gaps are; pair with Recognising Login Pages.',
        anchor: { industry: null, platform: null, target: 0.30, format: 'percent', goodBelow: true },
    },
    {
        id: 'training-debt', label: 'Training debt', weight: 15,
        meaning: 'of assigned courses are overdue, among users with training assigned',
        description: 'Overdue courses divided by total courses assigned in the rolling 6-month window. Reminders are not counted.',
        soWhat: 'Send a manager-CC\'d reminder; the longer training stays open, the worse the next failure rate gets.',
        anchor: { industry: null, platform: null, target: 0.10, format: 'percent', goodBelow: true },
    },
    {
        id: 'course-lag', label: 'Course lag', weight: 10,
        meaning: 'to complete a course after assignment',
        description: 'Average days to complete a course assigned in the rolling 6-month window, capped at 30. Higher = slower learner.',
        soWhat: 'Org-average lag should be under 7 days. Shorten the due window or trim course length if this stays high.',
        anchor: { industry: null, platform: null, target: 7 / 30, format: 'days', goodBelow: true },
    },
];

// Format a signal anchor for display alongside a component value. Prefers the
// industry anchor when present, falling back to the platform benchmark.
export function formatAnchor(anchor, value) {
    if (!anchor) return null;
    const ref = anchor.industry ?? anchor.platform;
    if (ref == null) return null;
    const refLabel = anchor.industry != null ? 'industry' : 'platform avg';
    if (anchor.format === 'percent') return `${Math.round(value * 100)}% vs ${Math.round(ref * 100)}% ${refLabel}`;
    if (anchor.format === 'days')    return `${Math.round(value * 30)} days vs ${Math.round(ref * 30)} days ${refLabel}`;
    return null;
}

// -- Helpers ---------------------------------------------------------------
function daysBetween(a, b) {
    return Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}

function inWindow(dateStr, today, days) {
    if (!dateStr) return false;
    const d = new Date(dateStr + 'T00:00:00Z');
    const diff = daysBetween(today, d);
    return diff >= 0 && diff <= days;
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// Map a sim status to severity. Live statuses are 'clicked' (DateFailed) and
// 'clicked_2nd' (LandingPageClicked); 'attachment' has no telemetry on the
// platform yet but stays in the lookup so it's wired up when added.
// Two-stage v2 scale matching the server model: any first-stage fail 0.5,
// landing page reached 1.0.
const STAGE_DEPTH_MAP = {
    clicked:      0.5,
    attachment:   0.5,
    clicked_2nd:  1.0,
};

// Per-user activity indexes, rebuilt on every recompute.
let SIM_BY_EMAIL = new Map();
let COURSE_BY_EMAIL = new Map();

function rebuildActivityIndexes() {
    SIM_BY_EMAIL = new Map();
    COURSE_BY_EMAIL = new Map();
    for (const e of SEND_LOG) {
        if (!SIM_BY_EMAIL.has(e.email)) SIM_BY_EMAIL.set(e.email, []);
        SIM_BY_EMAIL.get(e.email).push(e);
    }
    for (const e of COURSE_SEND_LOG) {
        if (!COURSE_BY_EMAIL.has(e.email)) COURSE_BY_EMAIL.set(e.email, []);
        COURSE_BY_EMAIL.get(e.email).push(e);
    }
}
rebuildActivityIndexes();

// -- Signal calculators ----------------------------------------------------
function computeFailRate(sims) {
    if (sims.length === 0) return 0.5; // neutral when no sim activity in window
    const failed = sims.filter((s) => s.status === 'clicked' || s.status === 'clicked_2nd' || s.status === 'attachment').length;
    return failed / sims.length;
}

// Longest run of consecutive clicks across a user's tests, ordered by send
// date. Shared definition between the repeat-offender factor and the Repeat
// Clickers metric.
export function longestClickStreak(sims) {
    if (!sims || sims.length === 0) return 0;
    const ordered = [...sims].sort(
        (a, b) => new Date(a.dateSent + 'T00:00:00Z').getTime() - new Date(b.dateSent + 'T00:00:00Z').getTime()
    );
    let run = 0;
    let longest = 0;
    for (const s of ordered) {
        const clicked = s.status === 'clicked' || s.status === 'clicked_2nd' || s.status === 'attachment';
        run = clicked ? run + 1 : 0;
        if (run > longest) longest = run;
    }
    return longest;
}

// Component (0-1) = share of the user's tests inside a consecutive click run
// of length >= 2.
function computeRepeatOffender(sims) {
    if (sims.length === 0) return 0;
    const ordered = [...sims].sort(
        (a, b) => new Date(a.dateSent + 'T00:00:00Z').getTime() - new Date(b.dateSent + 'T00:00:00Z').getTime()
    );
    const clicked = (s) => s.status === 'clicked' || s.status === 'clicked_2nd' || s.status === 'attachment';
    let run = 0;
    let inStreak = 0;
    for (const s of ordered) {
        if (!clicked(s)) { run = 0; continue; }
        run += 1;
        if (run === 2) inStreak += 2;
        else if (run > 2) inStreak += 1;
    }
    return clamp(inStreak / ordered.length, 0, 1);
}

function computeStageDepth(sims) {
    const fails = sims.filter((s) => s.status === 'clicked' || s.status === 'clicked_2nd' || s.status === 'attachment');
    if (fails.length === 0) return 0;
    return fails.reduce((m, s) => Math.max(m, STAGE_DEPTH_MAP[s.status] || 0), 0);
}

// A completion only "counts" as of `today` when it happened on or before
// `today` — so historical-window recomputations (previousBand, the 6-month
// trends) don't leak future completions and flatter the past.
function completedAsOf(c, today) {
    if (!c.dateCompleted) return false;
    return new Date(c.dateCompleted + 'T00:00:00Z').getTime() <= today.getTime();
}

function computeTrainingDebt(courses, today) {
    if (courses.length === 0) return 0.5;
    const overdue = courses.filter((c) => {
        if (completedAsOf(c, today)) return false;
        const sent = new Date(c.dateSent + 'T00:00:00Z');
        return daysBetween(today, sent) > COURSE_DUE_DAYS;
    }).length;
    return overdue / courses.length;
}

function computeCourseLag(courses, today) {
    const completed = courses.filter((c) => completedAsOf(c, today));
    if (completed.length === 0) return 0.5;
    const totalDays = completed.reduce((sum, c) => {
        const sent = new Date(c.dateSent + 'T00:00:00Z');
        const done = new Date(c.dateCompleted + 'T00:00:00Z');
        return sum + Math.max(0, daysBetween(done, sent));
    }, 0);
    const avg = totalDays / completed.length;
    return Math.min(avg / 30, 1.0);
}

function componentsFor(sims, courses, today) {
    return {
        'fail-rate':     computeFailRate(sims),
        'repeat':        computeRepeatOffender(sims),
        'stage-depth':   computeStageDepth(sims),
        'training-debt': computeTrainingDebt(courses, today),
        'course-lag':    computeCourseLag(courses, today),
    };
}

// Previous-cycle band: the model recomputed against a window ending 30 days
// earlier, banded on the raw base score (no multiplier - avoids recursion).
// Neutral Medium when the previous window has too little activity.
function previousBandAt(email, today) {
    const prevToday = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sims = (SIM_BY_EMAIL.get(email) || []).filter((s) => inWindow(s.dateSent, prevToday, WINDOW_DAYS));
    if (sims.length < MIN_SIMS_FOR_SCORE) return BANDS[1];
    const courses = (COURSE_BY_EMAIL.get(email) || []).filter((c) => inWindow(c.dateSent, prevToday, WINDOW_DAYS));
    const components = componentsFor(sims, courses, prevToday);
    const base = SIGNALS.reduce((sum, s) => sum + s.weight * components[s.id], 0);
    return bandFor(Math.round(clamp(base, 0, 100))) || BANDS[1];
}

// -- Per-user breakdown ----------------------------------------------------
export function computeBreakdown(email, today = DATA_TODAY) {
    const allSims = SIM_BY_EMAIL.get(email) || [];
    const allCourses = COURSE_BY_EMAIL.get(email) || [];
    const sims = allSims.filter((s) => inWindow(s.dateSent, today, WINDOW_DAYS));
    const courses = allCourses.filter((c) => inWindow(c.dateSent, today, WINDOW_DAYS));

    if (sims.length < MIN_SIMS_FOR_SCORE) {
        return { email, insufficient: true, simsInWindow: sims.length, signals: [], baseScore: null, finalScore: null, band: null, multiplier: null, previousBand: null };
    }

    const components = componentsFor(sims, courses, today);

    const signals = SIGNALS.map((s) => {
        const component = components[s.id];
        const baseContribution = s.weight * component;
        return { ...s, component, baseContribution };
    });
    const baseScore = signals.reduce((sum, s) => sum + s.baseContribution, 0);

    const previousBand = previousBandAt(email, today);
    const multiplier = previousBand.multiplier;

    const adjusted = baseScore * multiplier;
    const finalScore = Math.round(clamp(adjusted, 0, 100));
    const band = bandFor(finalScore);

    const finalSignals = signals.map((s) => ({
        ...s,
        finalContribution: s.baseContribution * multiplier,
    }));

    return {
        email,
        insufficient: false,
        simsInWindow: sims.length,
        coursesInWindow: courses.length,
        signals: finalSignals,
        baseScore: Math.round(baseScore * 10) / 10,
        multiplier,
        previousBand,
        finalScore,
        band,
        topDriver: [...finalSignals].sort((a, b) => b.finalContribution - a.finalContribution)[0],
    };
}

// Org score = plain mean of scorable user scores. The v2 volume floor was
// removed 2026-08: it overrode the mean for most tenancies, making the
// user-level scores redundant in the org number.
function orgScoreFromBreakdowns(breakdowns) {
    const scorable = breakdowns.filter((b) => !b.insufficient);
    if (scorable.length === 0) return null;
    return Math.round(scorable.reduce((sum, u) => sum + u.finalScore, 0) / scorable.length);
}

// -- Factor routing ----------------------------------------------------------
const FACTOR_DRILLS = {
    'fail-rate':     { page: 'factor-detail', label: 'Failure rate',     value: 'fail-rate' },
    'repeat':        { page: 'factor-detail', label: 'Repeat offender',  value: 'repeat' },
    'stage-depth':   { page: 'factor-detail', label: 'Stage depth',      value: 'stage-depth' },
    'training-debt': { page: 'factor-detail', label: 'Training debt',    value: 'training-debt' },
    'course-lag':    { page: 'factor-detail', label: 'Course lag',       value: 'course-lag' },
};

export const FACTOR_SOURCE_PAGE = {
    'fail-rate':     { page: 'sims',    label: 'Simulations' },
    'repeat':        { page: 'sims',    label: 'Simulations' },
    'stage-depth':   { page: 'sims',    label: 'Simulations' },
    'training-debt': { page: 'courses', label: 'Courses' },
    'course-lag':    { page: 'courses', label: 'Courses' },
};

export function formatFactorValue(signal, value) {
    const a = signal && signal.anchor;
    if (!a) return `${Math.round(value * 100)}%`;
    if (a.format === 'percent') return `${Math.round(value * 100)}%`;
    if (a.format === 'days')    return `${(value * 30).toFixed(1)}d`;
    if (a.format === 'depth')   return value.toFixed(2);
    return `${Math.round(value * 100)}%`;
}

// -- Live derived exports ----------------------------------------------------
export let USER_SCORES = [];
export let ORG_ACTIVE_USERS = 0;
export let ORG_SCORE = null;
export let ORG_BAND = null;
export let BENCHMARK = { you: null, industry: null, industryAvg: null, platformAvg: null, top25: null, youBand: null, cohortSize: 0 };
export let PREV_ORG_SCORE = null;
export let ORG_DELTA = 0;
export let ORG_TREND = [];
export let TOP_RISK_USERS = [];
export let ACTIVE_USERS = 0;
export let UNSCORED_USERS = 0;
export let ORG_FACTOR_STATS = [];

let SCORABLE = [];
let USER_META_BY_EMAIL = new Map();
// Month-end breakdown sets for the org/factor trends (index 0 = oldest).
let MONTHLY_BREAKDOWNS = [];
let MONTHLY_LABELS = [];

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function quantile(sorted, q) {
    if (sorted.length === 0) return null;
    const pos = (sorted.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    if (lo === hi) return sorted[lo];
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// Platform anchors from the anonymous cross-tenancy benchmark aggregation.
// Per-tenancy factor values are tenancy-grain approximations of the per-user
// model (documented in the data-gap notes); the same 35/20/20/15/10 weights
// produce a comparable composite per tenancy.
function computePlatformBenchmark(payload) {
    const rows = (payload?.tenancies || []).filter((r) => r && r.sends > 0);
    if (rows.length === 0) {
        return { platformAvg: null, top25: null, cohortSize: 0, factorMeans: {} };
    }
    const perTenancy = rows.map((r) => {
        const fail = r.clicks / r.sends;
        const repeat = clamp(Math.max(0, r.clicks - r.failingUsers) / r.sends, 0, 1);
        const stage = r.clicks > 0
            ? (0.33 * Math.max(0, r.clicks - r.landings) + 1.0 * Math.min(r.landings, r.clicks)) / r.clicks
            : 0;
        const debt = r.courseSends > 0 ? r.courseOverdue / r.courseSends : 0.5;
        const lag = r.avgLagDays != null ? Math.min(r.avgLagDays / 30, 1) : 0.5;
        return {
            'fail-rate': fail, 'repeat': repeat, 'stage-depth': stage,
            'training-debt': debt, 'course-lag': lag,
            score: clamp(35 * fail + 20 * repeat + 20 * stage + 15 * debt + 10 * lag, 0, 100),
        };
    });
    const mean = (key) => perTenancy.reduce((s, t) => s + t[key], 0) / perTenancy.length;
    const scores = perTenancy.map((t) => t.score).sort((a, b) => a - b);
    return {
        platformAvg: Math.round(mean('score')),
        top25: Math.round(quantile(scores, 0.25)),
        cohortSize: payload.cohortSize ?? rows.length,
        factorMeans: {
            'fail-rate': mean('fail-rate'),
            'repeat': mean('repeat'),
            'stage-depth': mean('stage-depth'),
            'training-debt': mean('training-debt'),
            'course-lag': mean('course-lag'),
        },
    };
}

// Rebuilds every derived export over the currently hydrated ./data rows.
// `benchmarkPayload` is response.benchmark from the dashboard endpoint's
// 'benchmark' aggregation (or null to keep platform anchors empty).
export function recomputeRiskModel(benchmarkPayload = null) {
    DATA_TODAY = new Date();
    rebuildActivityIndexes();
    USER_META_BY_EMAIL = new Map(USERS.map((u) => [u.email, u]));

    // Platform anchors first so per-user signal copies carry them.
    const platform = computePlatformBenchmark(benchmarkPayload);
    for (const sig of SIGNALS) {
        sig.anchor.platform = platform.factorMeans[sig.id] ?? null;
        sig.anchor.industry = null; // no industry data source on the platform
    }

    USER_SCORES = USERS.map((u) => {
        const b = computeBreakdown(u.email);
        return {
            email: u.email,
            name: u.name,
            firstName: u.firstName,
            lastName: u.lastName,
            dept: u.dept,
            location: u.location,
            ...b,
        };
    });
    SCORABLE = USER_SCORES.filter((u) => !u.insufficient);

    ORG_ACTIVE_USERS = SCORABLE.length;
    ORG_SCORE = orgScoreFromBreakdowns(SCORABLE);
    ORG_BAND = bandFor(ORG_SCORE);

    BENCHMARK = {
        you: ORG_SCORE,
        industry: null,       // no tenancy industry assignment exists on the platform
        industryAvg: null,
        platformAvg: platform.platformAvg,
        top25: platform.top25,
        youBand: ORG_BAND,
        cohortSize: platform.cohortSize,
    };

    // Previous-period org score (window shifted 30 days) for the delta.
    const PREV_TODAY = new Date(DATA_TODAY.getTime() - 30 * 24 * 60 * 60 * 1000);
    PREV_ORG_SCORE = orgScoreFromBreakdowns(USERS.map((u) => computeBreakdown(u.email, PREV_TODAY)));
    ORG_DELTA = (ORG_SCORE != null && PREV_ORG_SCORE != null) ? ORG_SCORE - PREV_ORG_SCORE : 0;

    // Month-end breakdown sets for the 6-month trends - real recomputation
    // over the hydrated events instead of the prototype's synthesised walk.
    MONTHLY_BREAKDOWNS = [];
    MONTHLY_LABELS = [];
    for (let i = 5; i >= 0; i--) {
        const t = new Date(DATA_TODAY.getTime());
        t.setUTCMonth(t.getUTCMonth() - i);
        MONTHLY_LABELS.push(MONTH_NAMES[t.getUTCMonth()]);
        MONTHLY_BREAKDOWNS.push(USERS.map((u) => computeBreakdown(u.email, t)));
    }
    // Months with no scorable users are dropped, not backfilled with today's
    // value - history must only contain measured points.
    ORG_TREND = MONTHLY_BREAKDOWNS.map((breakdowns, idx) => ({
        label: MONTHLY_LABELS[idx],
        org: orgScoreFromBreakdowns(breakdowns),
        industry: null,
        platform: platform.platformAvg,
    })).filter((p) => p.org != null);

    TOP_RISK_USERS = [...SCORABLE].sort((a, b) => b.finalScore - a.finalScore);
    ACTIVE_USERS = SCORABLE.length;
    UNSCORED_USERS = USER_SCORES.length - SCORABLE.length;

    ORG_FACTOR_STATS = SIGNALS.map((sig) => {
        const isCourseFactor = sig.id === 'training-debt' || sig.id === 'course-lag';
        const values = SCORABLE.map((u) => {
            const s = u.signals.find((x) => x.id === sig.id);
            if (!s) return null;
            // Course factors: a user with no courses in the window carries a 0.5
            // scoring neutral, not a measurement - exclude from the factor mean.
            if (isCourseFactor && !(u.coursesInWindow > 0)) return null;
            return s.component;
        }).filter((v) => v != null);
        const mean = values.length === 0 ? 0 : values.reduce((s, v) => s + v, 0) / values.length;
        const avgContribution = SCORABLE.length === 0
            ? 0
            : SCORABLE.reduce((s, u) => {
                const x = u.signals.find((y) => y.id === sig.id);
                return s + (x ? x.finalContribution : 0);
            }, 0) / SCORABLE.length;
        const anchorRef = sig.anchor ? (sig.anchor.industry ?? sig.anchor.platform) : null;
        const usersAbove = anchorRef != null
            ? values.filter((v) => (sig.anchor.goodBelow ? v > anchorRef : v < anchorRef)).length
            : 0;
        return {
            id: sig.id,
            label: sig.label,
            weight: sig.weight,
            meaning: sig.meaning,
            description: sig.description,
            soWhat: sig.soWhat,
            anchor: sig.anchor,
            componentMean: mean,
            avgContribution,
            usersAbove,
            sampleSize: values.length,
            drill: FACTOR_DRILLS[sig.id] || null,
        };
    });
}

// Run once at import so every export is shaped before the first hydration.
recomputeRiskModel();

// -- Trends -------------------------------------------------------------------
// Real 6-month factor trend: org-wide mean of the factor component at each
// month-end window.
export function buildFactorTrend(factorId, points = 6) {
    const out = [];
    const start = Math.max(0, MONTHLY_BREAKDOWNS.length - points);
    const isCourseFactor = factorId === 'training-debt' || factorId === 'course-lag';
    for (let i = start; i < MONTHLY_BREAKDOWNS.length; i++) {
        const scorable = MONTHLY_BREAKDOWNS[i].filter((b) => !b.insufficient && (!isCourseFactor || b.coursesInWindow > 0));
        const values = scorable
            .map((b) => b.signals.find((s) => s.id === factorId)?.component)
            .filter((v) => v != null);
        // Months with no measured values are skipped, never backfilled with
        // today's mean - the chart shows only real history.
        if (values.length === 0) continue;
        out.push({
            label: MONTHLY_LABELS[i],
            value: clamp(values.reduce((s, v) => s + v, 0) / values.length, 0, 1),
        });
    }
    return out;
}

export function buildFactorTopContributors(factorId, limit = 12) {
    const out = [];
    for (const u of USER_SCORES) {
        if (u.insufficient) continue;
        const sig = u.signals.find((s) => s.id === factorId);
        if (!sig || sig.component <= 0) continue;
        const sims = (SIM_BY_EMAIL.get(u.email) || []).filter((s) => inWindow(s.dateSent, DATA_TODAY, WINDOW_DAYS));
        const courses = (COURSE_BY_EMAIL.get(u.email) || []).filter((c) => inWindow(c.dateSent, DATA_TODAY, WINDOW_DAYS));
        out.push({
            email: u.email,
            name: u.name,
            sent: sims.length,
            clicks: sims.filter((s) => s.status === 'clicked' || s.status === 'clicked_2nd' || s.status === 'attachment').length,
            secondStage: sims.filter((s) => s.status === 'clicked_2nd').length,
            reported: sims.filter((s) => s.status === 'reported').length,
            coursesSent: courses.length,
            coursesCompleted: courses.filter((c) => completedAsOf(c, DATA_TODAY)).length,
            component: sig.component,
            contribution: sig.finalContribution,
        });
    }
    out.sort((a, b) => b.component - a.component);
    return out.slice(0, limit);
}

export function buildFactorLeague(field, factorId) {
    const signal = SIGNALS.find((s) => s.id === factorId);
    if (!signal) return [];
    const buckets = new Map();
    for (const u of SCORABLE) {
        const meta = USER_META_BY_EMAIL.get(u.email) || u;
        const key = meta[field];
        if (key == null || key === '') continue;
        const sig = u.signals.find((s) => s.id === factorId);
        if (!sig) continue;
        let b = buckets.get(key);
        if (!b) { b = { name: key, sum: 0, users: 0 }; buckets.set(key, b); }
        b.sum += sig.component;
        b.users += 1;
    }
    const goodBelow = signal.anchor ? signal.anchor.goodBelow !== false : true;
    return [...buckets.values()]
        .map((b) => ({ name: b.name, users: b.users, value: b.sum / b.users }))
        .sort((a, b) => (goodBelow ? b.value - a.value : a.value - b.value));
}

// Per-user trend. Each point is the user's score computed against a window
// ending at that month. Months with insufficient activity are dropped, never
// invented; returns [] with fewer than 2 real points so the chart hides.
export function userTrend(email, months = 6) {
    const points = [];
    for (let i = months - 1; i >= 0; i--) {
        const t = new Date(DATA_TODAY.getTime());
        t.setUTCMonth(t.getUTCMonth() - i);
        const b = computeBreakdown(email, t);
        if (b.insufficient) continue;
        points.push({ label: MONTH_NAMES[t.getUTCMonth()], score: b.finalScore });
    }
    return points.length >= 2 ? points : [];
}

// Group 6-month trend: monthly average of finalScore across every scorable
// user in the slice, mirroring how MetaDetailPage's group score is the mean
// of per-user scores. Months where no user is scorable borrow the nearest
// scored month rather than inventing a drift; returns [] when no month has
// a scorable user so callers can hide the chart.
export function groupTrend(emails, months = 6) {
    const points = [];
    for (let i = months - 1; i >= 0; i--) {
        const t = new Date(DATA_TODAY.getTime());
        t.setUTCMonth(t.getUTCMonth() - i);
        const scores = [];
        for (const email of emails) {
            const b = computeBreakdown(email, t);
            if (!b.insufficient) scores.push(b.finalScore);
        }
        points.push({
            label: MONTH_NAMES[t.getUTCMonth()],
            score: scores.length ? Math.round(scores.reduce((a, s) => a + s, 0) / scores.length) : null,
        });
    }
    if (!points.some((p) => p.score != null)) return [];
    for (let i = 0; i < points.length; i++) {
        if (points[i].score != null) continue;
        const prev = [...points.slice(0, i)].reverse().find((p) => p.score != null);
        const next = points.slice(i + 1).find((p) => p.score != null);
        points[i].score = (prev || next).score;
    }
    return points;
}
